import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
// Slides
import { TitleSlide } from './slides/TitleSlide';
import { LandscapeSlide } from './slides/LandscapeSlide';
import { MarketShareSlide } from './slides/MarketShareSlide';
import { NvidiaDominanceSlide } from './slides/NvidiaDominanceSlide';
import { InferenceShiftSlide } from './slides/InferenceShiftSlide';
import { HyperscalerSlide } from './slides/HyperscalerSlide';
import { AsicProblemSlide } from './slides/AsicProblemSlide';
import { FpgaWinsSlide } from './slides/FpgaWinsSlide';
import { TokensPerWattSlide } from './slides/TokensPerWattSlide';
import { DecodeStageSlide } from './slides/DecodeStageSlide';
import { StreamingArchSlide } from './slides/StreamingArchSlide';
import { DataflowFusionSlide } from './slides/DataflowFusionSlide';
import { MemoryUtilSlide } from './slides/MemoryUtilSlide';
import { CustomHardwareSlide } from './slides/CustomHardwareSlide';
import { HonestCaveatSlide } from './slides/HonestCaveatSlide';
import { ComparisonSlide } from './slides/ComparisonSlide';
import { OpenFpgaAdvantageSlide } from './slides/OpenFpgaAdvantageSlide';
import { CtaSlide } from './slides/CtaSlide';
import { SourcesPage1Slide } from './slides/SourcesPage1Slide';
import { SourcesPage2Slide } from './slides/SourcesPage2Slide';
import { ExecSummarySlide } from './slides/ExecSummarySlide';
const SLIDES = [TitleSlide, ExecSummarySlide, LandscapeSlide, MarketShareSlide, NvidiaDominanceSlide, InferenceShiftSlide, HyperscalerSlide, AsicProblemSlide, FpgaWinsSlide, TokensPerWattSlide, DecodeStageSlide, StreamingArchSlide, DataflowFusionSlide, MemoryUtilSlide, CustomHardwareSlide, HonestCaveatSlide, ComparisonSlide, OpenFpgaAdvantageSlide, CtaSlide, SourcesPage1Slide, SourcesPage2Slide];
export function Presentation() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const nextSlide = useCallback(() => {
    if (currentSlide < SLIDES.length - 1) {
      setDirection(1);
      setCurrentSlide(prev => prev + 1);
    }
  }, [currentSlide]);
  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      setDirection(-1);
      setCurrentSlide(prev => prev - 1);
    }
  }, [currentSlide]);
  // Touch swipe handling
  const touchStartX = useRef<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (touchStartX.current === null || touchStartY.current === null) return;
    const deltaX = touchStartX.current - e.changedTouches[0].clientX;
    const deltaY = touchStartY.current - e.changedTouches[0].clientY;
    // Only trigger slide change if horizontal movement dominates and exceeds threshold
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      deltaX > 0 ? nextSlide() : prevSlide();
    }
    touchStartX.current = null;
    touchStartY.current = null;
  }, [nextSlide, prevSlide]);
  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Space') {
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        prevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);
  const CurrentSlideComponent = SLIDES[currentSlide];
  return <div className="relative w-full h-full overflow-hidden bg-brand-dark text-white" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd} data-id="element-10">
      {/* Slide Content */}
      <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-11">
        <motion.div key={currentSlide} custom={direction} initial={{
        opacity: 0,
        x: direction > 0 ? 100 : -100
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: direction > 0 ? -100 : 100
      }} transition={{
        duration: 0.4,
        ease: 'easeInOut'
      }} className="absolute inset-0 w-full h-full" data-id="element-12">
          <CurrentSlideComponent isActive={true} data-id="element-13" />
        </motion.div>
      </AnimatePresence>

      {/* Navigation Controls */}
      <div className="absolute inset-0 pointer-events-none flex justify-between items-center px-4 z-50" data-id="element-14">
        <button onClick={prevSlide} disabled={currentSlide === 0} className={`p-3 rounded-full bg-brand-surface/50 hover:bg-brand-surface text-white transition-all pointer-events-auto ${currentSlide === 0 ? 'opacity-0' : 'opacity-100'}`} data-id="element-15">
          <ChevronLeft size={32} data-id="element-16" />
        </button>
        <button onClick={nextSlide} disabled={currentSlide === SLIDES.length - 1} className={`p-3 rounded-full bg-brand-surface/50 hover:bg-brand-surface text-white transition-all pointer-events-auto ${currentSlide === SLIDES.length - 1 ? 'opacity-0' : 'opacity-100'}`} data-id="element-17">
          <ChevronRight size={32} data-id="element-18" />
        </button>
      </div>

      {/* Progress Dots */}
      <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-2 z-50 pointer-events-none" data-id="element-19">
        {SLIDES.map((_, index) => <div key={index} className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentSlide ? 'bg-brand-primary w-6' : 'bg-brand-border'}`} data-id="element-20" />)}
      </div>
    </div>;
}